/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { SUPPORTED_LOCALES, TIZEN_PLATFORM } from '../../config';
import { Log } from '../logging/Log';
import { Storage } from '../storage/Storage';
import { MessageService } from './MessageService';

export class TvProperties {
    private oTvProperties: any = {};

    private static TAG = 'TvProperties';

    private supportedLanguages = ['en-US', 'es-US'];

    initialize = async () => {
        return new Promise((resolve, reject) => {
            new MessageService(MessageService.GET_TV_PROPERTIES)
                .getResponse()
                .then((response) => {
                    Log.i(TvProperties.TAG, 'Got the TV Properties', response);
                    this.oTvProperties = <JSON>response;
                    if (
                        this.oTvProperties?.dateLocale &&
                        !this.supportedLanguages.includes(this.oTvProperties?.dateLocale)
                    ) {
                        this.oTvProperties.dateLocale = 'en-US';
                    }
                    resolve(true);
                })
                .catch((error) => {
                    Log.e(TvProperties.TAG, 'Failed to initialize TV Properties.', error);
                    reject(error);
                });
        });
    };

    disableScreenSaver() {
        if (this.getPlatformName() === TIZEN_PLATFORM) {
            new MessageService(MessageService.DISABLE_SCREENSAVER).getResponse().catch((error) => {
                Log.e(TvProperties.TAG, error);
            });
        }
    }

    enableScreenSaver() {
        if (this.getPlatformName() === TIZEN_PLATFORM) {
            new MessageService(MessageService.ENABLE_SCREENSAVER).getResponse().catch((error) => {
                Log.e(TvProperties.TAG, error);
            });
        }
    }

    getActiveConnectionType = () => {
        return (this.oTvProperties && this.oTvProperties.activeConnectionType) || 'Unknown';
    };

    getDateLocale = () => {
        const dateLocale = Storage.getInstance(Storage.StorageType.LOCAL_STORAGE)
            .getItem(Storage.Keys.LOCALE)
            ?.replace('_', '-');
        return dateLocale || (this.oTvProperties && this.oTvProperties.dateLocale) || 'en-US';
    };

    getDeviceName = () => {
        return (this.oTvProperties && this.oTvProperties.deviceName) || 'Unknown';
    };

    getDeviceModel = () => {
        return (this.oTvProperties && this.oTvProperties.deviceModel) || 'Unknown';
    };

    getDeviceModelCode = () => {
        return (this.oTvProperties && this.oTvProperties.deviceModelCode) || 'Unknown';
    };

    getDeviceTotalMemory = () => {
        return (this.oTvProperties && this.oTvProperties.deviceTotalMemory) || 'Unknown';
    };

    getHsn = () => {
        return (this.oTvProperties && this.oTvProperties.hsn) || 'Unknown';
    };

    getIpAddress = () => {
        return (this.oTvProperties && this.oTvProperties.ipAddress) || 'Unknown';
    };

    getLocale = () => {
        const locale = Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).getItem(
            Storage.Keys.LOCALE,
        );
        return locale || (this.oTvProperties && this.oTvProperties.locale) || 'en_US';
    };

    getLang = () => {
        const lang = Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).getItem(
            Storage.Keys.LANGUAGE,
        );
        return lang || (this.oTvProperties && this.oTvProperties.lang) || 'en';
    };

    getMacAddress() {
        return (this.oTvProperties && this.oTvProperties.macAddress) || '00:11:22:33:44:55';
    }

    getPlatformVersion() {
        return (this.oTvProperties && this.oTvProperties.platformVersion) || 'Unknown';
    }

    getPlatformName() {
        return (this.oTvProperties && this.oTvProperties.platformName) || 'Unknown';
    }

    getRegion() {
        return (this.oTvProperties && this.oTvProperties.region) || 'US';
    }

    /**
     * this function should be used in places where device information is to be provided
     * Ex. Logging, device info in v1/iptvLogin, etc.
     * @returns string {current time zone of device}
     */
    getTimezone() {
        return (this.oTvProperties && this.oTvProperties.timezone) || 'PDT';
    }

    /**
     * this function should be used as time zone value when you request any
     * date time related data from service api.
     * service api will then return data according to GMT and when we convert that time
     * into date object using Date constructor, it will automatically take care of
     * converting GMT time to current time zone
     * @returns string {GMT}
     */
    getDefaultTimeZone = () => {
        return 'GMT';
    };

    /**
     * Retrieves the Olson time zone format (IANA time zone) of the current system.
     *
     * @returns {string} The Olson time zone string (e.g., "Asia/Kolkata", "America/New_York").
     *
     * @example
     * const timeZone = TvProperties.getOlsonTimeZone();
     * console.log(timeZone); // Example Output: "Asia/Kolkata"
     */
    public getOlsonTimeZone = () => {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    };

    hasSupportedLanguage = () => {
        const currentLang = this.getLang();
        const isLanguageSupported = SUPPORTED_LOCALES.some((item) => item.language === currentLang);
        return isLanguageSupported;
    };
}
